import { Injectable, signal, computed } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  User,
  AuthState,
  DynamicCompanyTokens,
  CompanyTokenData
} from '../models/dynamic-company.models';
import { parseESTToUTC } from '@shared/utils/date.utils';

@Injectable({
  providedIn: 'root'
})
export class AuthStateService {
  // Private state signals
  private authState = signal<AuthState>({
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: false,
    currentCompany: null,
    isCustomerLogin: false,
    error: null,
    lastActivity: null,
    sessionExpiry: null
  });

  constructor() {
    // Initialize non-sensitive auth context from storage.
    this.initializeFromStorage();
  }

  // Public computed signals
  readonly user = computed(() => this.authState().user);
  readonly isAuthenticated = computed(() => this.authState().isAuthenticated);
  readonly isLoading = computed(() => this.authState().isLoading);
  readonly permissions = computed(() => this.authState().user?.permissions || []);
  readonly currentCompany = computed(() => this.authState().currentCompany);
  readonly isCustomerLogin = computed(() => this.authState().isCustomerLogin);
  readonly error = computed(() => this.authState().error);
  readonly lastActivity = computed(() => this.authState().lastActivity);
  readonly sessionExpiry = computed(() => this.authState().sessionExpiry);
  readonly token = computed(() => this.authState().token);

  // Company tokens (dynamic for any company)
  private companyTokens = signal<DynamicCompanyTokens>({});

  // Computed for available companies
  readonly availableCompanies = computed(() => Object.keys(this.companyTokens()));
  readonly hasMultipleCompanies = computed(() => this.availableCompanies().length > 1);

  // Observable streams for reactive programming
  private authStateSubject = new BehaviorSubject<AuthState>(this.authState());

  /**
   * Get observable stream of auth state
   */
  get authState$(): Observable<AuthState> {
    return this.authStateSubject.asObservable();
  }

  /**
   * Update authentication state
   */
  setAuthState(updates: Partial<AuthState>): void {
    const currentState = this.authState();
    const newState = { ...currentState, ...updates };

    this.authState.set(newState);
    this.authStateSubject.next(newState);
  }

  /**
   * Set user data
   */
  setUser(user: User | null): void {
    this.setAuthState({ user });
  }

  /**
   * Set authentication status
   */
  setAuthenticated(isAuthenticated: boolean, token?: string): void {
    this.setAuthState({
      isAuthenticated,
      token: token || this.authState().token
    });
  }

  /**
   * Set loading state
   */
  setLoading(isLoading: boolean): void {
    this.setAuthState({ isLoading });
  }

  /**
   * Set current company
   */
  setCurrentCompany(company: string | null): void {
    this.setAuthState({ currentCompany: company });
  }

  /**
   * Set customer login status
   */
  setCustomerLogin(isCustomerLogin: boolean): void {
    this.setAuthState({ isCustomerLogin });
  }

  /**
   * Set error state
   */
  setError(error: string | null): void {
    this.setAuthState({ error });
  }

  /**
   * Update last activity timestamp
   */
  updateLastActivity(): void {
    this.setAuthState({ lastActivity: new Date() });
  }

  /**
   * Set session expiry time
   */
  setSessionExpiry(expiry: Date | null): void {
    this.setAuthState({ sessionExpiry: expiry });
  }

  /**
   * Set company tokens (dynamic)
   */
  setCompanyTokens(tokens: DynamicCompanyTokens): void {
    this.companyTokens.set(tokens);
  }

  /**
   * Update specific company token (dynamic)
   */
  updateCompanyToken(companyName: string, tokenData: CompanyTokenData): void {
    const current = this.companyTokens();
    this.companyTokens.set({
      ...current,
      [companyName]: tokenData
    });
  }

  /**
   * Get company tokens
   */
  getCompanyTokens(): DynamicCompanyTokens {
    return this.companyTokens();
  }

  /**
   * Get token for specific company (dynamic)
   */
  getCompanyToken(companyName: string): string | undefined {
    return this.companyTokens()[companyName]?.token;
  }

  /**
   * Get company token data for specific company
   */
  getCompanyTokenData(companyName: string): CompanyTokenData | undefined {
    return this.companyTokens()[companyName];
  }

  /**
   * Check if user has specific permission
   */
  hasPermission(permission: string): boolean {
    return this.permissions().includes(permission);
  }

  /**
   * Check if user has specific role
   */
  hasRole(role: string): boolean {
    return this.user()?.role === role;
  }

  /**
   * Get user ID
   */
  getUserId(): number {
    return this.user()?.userID || 0;
  }

  /**
   * Get username
   */
  getUserName(): string {
    return this.user()?.userName || '';
  }

  /**
   * Check if session is expired
   */
  isSessionExpired(): boolean {
    const expiry = this.sessionExpiry();
    if (!expiry) return false;

    return new Date() > expiry;
  }

  /**
   * Get time until session expires (in milliseconds)
   */
  getTimeToExpiry(): number {
    const expiry = this.sessionExpiry();
    if (!expiry) return 0;

    return Math.max(0, expiry.getTime() - new Date().getTime());
  }

  /**
   * Check if session expires soon (within specified minutes)
   */
  isSessionExpiringSoon(warningMinutes: number = 5): boolean {
    const timeToExpiry = this.getTimeToExpiry();
    const warningTime = warningMinutes * 60 * 1000; // Convert to milliseconds

    return timeToExpiry > 0 && timeToExpiry <= warningTime;
  }

  /**
   * Reset all state to initial values
   */
  reset(): void {
    const initialState: AuthState = {
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      currentCompany: null,
      isCustomerLogin: false,
      error: null,
      lastActivity: null,
      sessionExpiry: null
    };

    this.authState.set(initialState);
    this.authStateSubject.next(initialState);
    this.companyTokens.set({});
  }

  /**
   * Initialize authentication state from localStorage on app startup
   */
  private initializeFromStorage(): void {
    try {
      const currentCompany = localStorage.getItem('currentCompany') || 'unity';
      const isLoggedIn = JSON.parse(localStorage.getItem('isLoggedIn') || 'false');
      const isCustomerLogin = JSON.parse(localStorage.getItem('isCustomerLogin') || 'false');

      // Try to find user details for the current company
      const userKey = `tibss.${currentCompany}-userDetails`;
      const userStr = localStorage.getItem(userKey) || localStorage.getItem('userDetails');

      if (!isLoggedIn || !isCustomerLogin) {
        // If flags are false but user data still exists, the flags are stale.
        // Let AuthService.initializeAuth() handle recovery — just load what we can.
        if (!userStr) {
          this.reset();
          return;
        }
      }

      if (!userStr) {
        this.reset();
        return;
      }

      const user = JSON.parse(userStr);

      // Access token is intentionally memory-only and rehydrated by AuthService.
      this.authState.set({
        user,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        currentCompany,
        isCustomerLogin: true,
        error: null,
        lastActivity: null,
        sessionExpiry: null
      });
    } catch (error) {
      console.error('Error initializing auth state from storage:', error);
      this.reset();
    }
  }

  /**
   * Legacy helper retained for compatibility. Tokens are no longer persisted.
   */
  private isStoredTokenExpired(_companyName: string): boolean {
    return false;
  }

  /**
   * Check if refresh token is expired - matches old system logic
   */
  private isRefreshTokenExpired(companyName: string): boolean {
    const tokenData = this.getCompanyTokenData(companyName);
    const expiry = tokenData?.refreshExpiresAt;
    if (!expiry) return false;
    return expiry.getTime() <= Date.now();
  }

  /**
   * Set refresh token with backend-provided expiry (EST format)
   */
  setRefreshToken(companyName: string, refreshToken: string, refreshTokenExpiry?: string): void {
    let expiryDate = new Date();
    if (refreshTokenExpiry) {
      expiryDate = this.safeParseTokenExpiry(refreshTokenExpiry) || expiryDate;
    }

    // Update in-memory state only.
    const current = this.companyTokens();
    const existingTokenData = current[companyName];
    if (existingTokenData) {
      const updatedTokenData: CompanyTokenData = {
        ...existingTokenData,
        refreshToken,
        refreshExpiresAt: expiryDate
      };
      this.updateCompanyToken(companyName, updatedTokenData);
    }
  }

  /**
   * Check if refresh token is about to expire (matches old system)
   */
  isRefreshTokenAboutToExpire(companyName: string, warningMinutes: number = 5): boolean {
    const expiryDate = this.getCompanyTokenData(companyName)?.refreshExpiresAt;
    if (!expiryDate) return false;

    const now = new Date();
    const diff = (expiryDate.getTime() - now.getTime()) / (1000 * 60); // in minutes
    
    return diff < warningMinutes && diff > 0.5; // Within warning time but not yet expired
  }

  /**
   * Safely parse token expiry date - handles multiple formats:
   * 1. API format: "2025-09-17 03:53:50" (backend datetime without timezone)
   * 2. Current storage: "2025-09-16T22:25:50.000Z" (ISO UTC format)
   * 3. Old storage: "Wed Sep 17 2025 13:23:49 GMT+0530 (India Standard Time)" (JS Date.toString())
   */
  private safeParseTokenExpiry(expiryStr: string | null): Date | null {
    if (!expiryStr) return null;
    try {
      // Remove quotes if present
      const normalized = expiryStr.replace(/^"|"$/g, "");

 

      // Handle EST/EDT explicitly (API might send these)
      if (/(EST|EDT)$/.test(normalized)) {
 
        return parseESTToUTC(normalized);
      }

      // Check if this is old format with GMT timezone info (from old codebase)
      // Format: "Wed Sep 17 2025 13:23:49 GMT+0530 (India Standard Time)"
      if (normalized.includes('GMT') || normalized.includes('India Standard Time')) {
  
        const parsed = new Date(normalized);
        if (!isNaN(parsed.getTime()) && parsed.getTime() > 0) {

          return parsed;
        }
      }

      // Check if this is current storage format (ISO with Z)
      // Format: "2025-09-16T22:25:50.000Z"
      if (normalized.includes('T') && (normalized.endsWith('Z') || normalized.includes('+') || normalized.includes('-'))) {

        const parsed = new Date(normalized);
        if (!isNaN(parsed.getTime()) && parsed.getTime() > 0) {
    
          return parsed;
        }
      }

      // Check if this is API format without timezone
      // Format: "2025-09-17 03:53:50" (backend datetime, assume EST)
      if (normalized.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/)) {
        // Convert to ISO format and assume EST (UTC-5)
        const dateToparse = normalized.replace(' ', 'T') + '-05:00';
        const parsed = new Date(dateToparse);
        if (!isNaN(parsed.getTime()) && parsed.getTime() > 0) {
          return parsed;
        }
      }

      // Try native Date parsing as fallback for any other format

      const nativeParsed = new Date(normalized);
      if (!isNaN(nativeParsed.getTime()) && nativeParsed.getTime() > 0) {
        // Validate the date is reasonable (within 48 hours range)
        const now = new Date();
        const diffHours = Math.abs(nativeParsed.getTime() - now.getTime()) / (1000 * 60 * 60);
        if (diffHours < 48) {
      
          return nativeParsed;
        } else {

        }
      }

      // Final fallback: try multiple format attempts
      const fallbackFormats = [
        normalized, // Original
        normalized.replace(' ', 'T') + '-05:00', // Convert to EST
        normalized.replace(' ', 'T') + 'Z', // Convert to UTC
        normalized.replace(' ', 'T'), // ISO without timezone
        normalized + 'Z' // Add Z if missing
      ];

      for (const format of fallbackFormats) {
        try {
          const attemptParsed = new Date(format);
          if (!isNaN(attemptParsed.getTime()) && attemptParsed.getTime() > 0) {
            const now = new Date();
            const diffHours = Math.abs(attemptParsed.getTime() - now.getTime()) / (1000 * 60 * 60);
            if (diffHours < 48) {
            
              return attemptParsed;
            }
          }
        } catch (formatError) {
          // Continue to next format
        }
      }

      console.warn('âŒ Failed to parse date with all methods:', expiryStr);
      return null;

    } catch (error) {
      console.warn('Error parsing token expiry date:', expiryStr, error);
      return null;
    }
  }

  /**
   * Clear stored authentication data
   */
  private clearStoredAuth(): void {
    const currentCompany = localStorage.getItem('currentCompany') || 'unity';
    
    // Clear company-specific data
    localStorage.removeItem(`tibss.${currentCompany}-userDetails`);
    
    // Clear global auth state
    localStorage.setItem('isLoggedIn', 'false');
    localStorage.setItem('isCustomerLogin', 'false');
    
    this.reset();
  }

  /**
   * Clear expired tokens for a specific company
   */
  clearExpiredTokens(companyName: string): void {
    if (this.isStoredTokenExpired(companyName) || this.isRefreshTokenExpired(companyName)) {

      
      localStorage.removeItem(`tibss.${companyName}-userDetails`);
      
      // Update signals
      const current = this.companyTokens();
      const updated = { ...current };
      delete updated[companyName];
      this.companyTokens.set(updated);
      
      // If this was the current company, clear main auth state
      if (this.currentCompany() === companyName) {
        this.clearStoredAuth();
      }
    }
  }

  /**
   * Get debugging information about current auth state
   */
  getAuthDebugInfo(): any {
    const currentCompany = this.currentCompany() || 'unity';
    const tokenData = this.getCompanyTokenData(currentCompany);
    
    return {
      currentCompany,
      isAuthenticated: this.isAuthenticated(),
      isCustomerLogin: this.isCustomerLogin(),
      hasToken: !!tokenData?.token,
      hasRefreshToken: !!tokenData?.refreshToken,
      tokenExpiry: tokenData?.expiresAt?.toISOString(),
      refreshTokenExpiry: tokenData?.refreshExpiresAt?.toISOString(),
      isTokenExpired: this.isStoredTokenExpired(currentCompany),
      isRefreshTokenExpired: this.isRefreshTokenExpired(currentCompany),
      localStorageKeys: {
        isLoggedIn: localStorage.getItem('isLoggedIn'),
        isCustomerLogin: localStorage.getItem('isCustomerLogin'),
        currentCompany: localStorage.getItem('currentCompany'),
        userDetails: !!localStorage.getItem(`tibss.${currentCompany}-userDetails`)
      }
    };
  }

  /**
   * Store fresh token data after login using backend-provided expiry (EST format)
   */
  storeLoginTokens(companyName: string, tokenData: {
    token: string;
    refreshToken: string;
    userId: number;
    accessTokenExpiry?: string;
    refreshTokenExpiry?: string;
  }): void {
    // Parse backend expiry dates (EST format: "2025-12-12 11:40:03")
    const accessExpiry = tokenData.accessTokenExpiry 
      ? this.safeParseTokenExpiry(tokenData.accessTokenExpiry) || new Date()
      : new Date();
    
    const refreshExpiry = tokenData.refreshTokenExpiry
      ? this.safeParseTokenExpiry(tokenData.refreshTokenExpiry) || new Date()
      : new Date();

    // Store in auth state (memory-only)
    const companyTokenData: CompanyTokenData = {
      token: tokenData.token,
      refreshToken: tokenData.refreshToken,
      userId: tokenData.userId,
      expiresAt: accessExpiry,
      refreshExpiresAt: refreshExpiry
    };

    this.updateCompanyToken(companyName, companyTokenData);

    this.validateTokenStorage(companyName);
  }

  /**
   * Debug method to validate token storage after login
   */
  validateTokenStorage(companyName: string): any {
    const tokenData = this.getCompanyTokenData(companyName);
    const user = localStorage.getItem(`tibss.${companyName}-userDetails`);
    
    const validation = {
      companyName,
      hasToken: !!tokenData?.token,
      hasRefreshToken: !!tokenData?.refreshToken,
      hasExpiry: !!tokenData?.refreshExpiresAt,
      hasUser: !!user,
      expiryValue: tokenData?.refreshExpiresAt?.toISOString(),
      parsedExpiry: tokenData?.refreshExpiresAt || null,
      isTokenExpired: this.isStoredTokenExpired(companyName),
      isRefreshTokenExpired: this.isRefreshTokenExpired(companyName),
      globalFlags: {
        isLoggedIn: localStorage.getItem('isLoggedIn'),
        isCustomerLogin: localStorage.getItem('isCustomerLogin'),
        currentCompany: localStorage.getItem('currentCompany')
      }
    };
    

    return validation;
  }

  /**
   * Initialize and clean up expired tokens for all companies
   */
  initializeAndCleanupTokens(): void {
    // Token data is memory-only now; no localStorage token cleanup required.
  }

  /**
   * Get current state snapshot
   */
  getSnapshot(): AuthState {
    return this.authState();
  }
}

